import { UcoastEl } from '@/scripts/core/UcoastEl'
import { TsDOM as q } from '@/scripts/core/TsDOM'

export class PasswordCountdown extends UcoastEl {
	static htmlSelector = 'password-countdown'

	display: HTMLElement
	message: HTMLElement | null
	targetUnixMs: number
	intervalId?: number

	constructor() {
		super()
		this.display = q.rs('[data-countdown-display]', this)
		this.message = this.querySelector('[data-countdown-message]')
		if (this.message) {
			this.classList.add('has-post-launch-message')
		}
		const targetDate = this.getAttribute('data-target-date') ?? ''
		const targetTime = this.getAttribute('data-target-time') ?? '00:00'
		const timezone =
			this.getAttribute('data-timezone') || 'America/Los_Angeles'
		this.targetUnixMs = PasswordCountdown.wallTimeToUnix(
			targetDate,
			targetTime,
			timezone
		)
	}

	connectedCallback() {
		super.connectedCallback()
		this.tick()
		this.intervalId = window.setInterval(() => this.tick(), 1000)
	}

	disconnectedCallback() {
		super.disconnectedCallback()
		if (this.intervalId) window.clearInterval(this.intervalId)
	}

	tick() {
		const now = Date.now()
		const diff = this.targetUnixMs - now
		if (Number.isNaN(this.targetUnixMs) || diff <= 0) {
			this.classList.add('is-complete')
			if (this.intervalId) {
				window.clearInterval(this.intervalId)
				this.intervalId = undefined
			}
			return
		}
		const days = Math.floor(diff / 86_400_000)
		const hours = Math.floor((diff % 86_400_000) / 3_600_000)
		const minutes = Math.floor((diff % 3_600_000) / 60_000)
		const seconds = Math.floor((diff % 60_000) / 1000)
		const pad = (n: number) => String(n).padStart(2, '0')
		this.display.textContent = `${pad(days)}:${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
	}

	static wallTimeToUnix(
		dateStr: string,
		timeStr: string,
		timezone: string
	): number {
		const [yearStr, monthStr, dayStr] = dateStr.split('-')
		const [hourStr, minuteStr] = timeStr.split(':')
		const year = Number(yearStr)
		const month = Number(monthStr)
		const day = Number(dayStr)
		const hour = Number(hourStr)
		const minute = Number(minuteStr)
		if (
			!Number.isFinite(year) ||
			!Number.isFinite(month) ||
			!Number.isFinite(day) ||
			!Number.isFinite(hour) ||
			!Number.isFinite(minute)
		) {
			return NaN
		}
		const naiveUtc = Date.UTC(year, month - 1, day, hour, minute, 0)
		const offsetMs = PasswordCountdown.timezoneOffsetMs(naiveUtc, timezone)
		return naiveUtc - offsetMs
	}

	static timezoneOffsetMs(unixMs: number, timezone: string): number {
		const dtf = new Intl.DateTimeFormat('en-US', {
			timeZone: timezone,
			hour12: false,
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
		})
		const parts = dtf.formatToParts(new Date(unixMs))
		const map: Record<string, number> = {}
		for (const part of parts) {
			if (part.type !== 'literal') {
				map[part.type] = parseInt(part.value, 10)
			}
		}
		let hour = map.hour ?? 0
		if (hour === 24) hour = 0
		const utcOfWall = Date.UTC(
			map.year ?? 1970,
			(map.month ?? 1) - 1,
			map.day ?? 1,
			hour,
			map.minute ?? 0,
			map.second ?? 0
		)
		return utcOfWall - unixMs
	}
}
